/**
 * gameBanner — the single source of truth that turns any game-like record into
 * the shared banner input (`GameRowInput`), which `toGameRowProps` then renders
 * via `GameSummaryRow`. Home, Games and Divisions all build their rows here so a
 * game reads and behaves identically everywhere.
 *
 * Three sources feed one bander shape:
 *   - `gameToRowInput`     — a hydrated `Game` DTO (carries `activeProposal`).
 *   - `proposalToRowInput` — a `GameProposal` (the negotiation view).
 *   - `summaryToRowInput`  — a cross-tenant `MyGameSummary` (the Home feed).
 */
import type { Game, GameProposal, MyGameSummary } from '@sulis-liiga/common';
import { GameStatus } from '@sulis-liiga/common';
import { teamLabelsOf, teamLabel, formatSetScores } from './gameTeams';
import { respondingTeamId, activeSlots } from './proposals';
import type { GameRowInput } from '../components/game/GameSummaryRow';

/** Viewer context for deciding participation and whose turn it is. */
export interface BannerContext {
  /** The viewer's team ids (empty when logged out). */
  myTeamIds: Set<string>;
  /** The viewer's user id (for "awaiting my approval"). */
  userId?: string;
}

/** Join the non-empty parts of a subtitle with " · ". */
const joinParts = (parts: Array<string | undefined | false>): string =>
  parts.filter(Boolean).join(' · ');

const formatDate = (d: Date | string): string =>
  new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * Build the banner input for a hydrated `Game`.
 *
 * @param options.showDivision include the division/category in the subtitle
 *   (off on the Divisions page, where the division is the section heading).
 * @param options.showDate include the date in the subtitle (on where there is no
 *   date group-header, e.g. the Divisions list).
 */
export function gameToRowInput(
  game: Game,
  ctx: BannerContext,
  options: { showDivision?: boolean; showDate?: boolean } = {},
): GameRowInput {
  const { showDivision = true, showDate = false } = options;
  const [a, b] = teamLabelsOf(game);
  // A fixture (PENDING/PROPOSED) has no schedule yet.
  const isFixture = game.status === GameStatus.PENDING || game.status === GameStatus.PROPOSED;
  const isFinished = game.status === GameStatus.COMPLETED || game.status === GameStatus.DISPUTED;
  const division = game.divisionName ?? game.divisionCategory ?? '';
  const dateStr = game.scheduledDate ? formatDate(game.scheduledDate) : undefined;

  // Fixtures show no schedule line. Finished games: division? · date? · venue.
  // In-progress games: division? · date? · time · venue · court. (if/else, not a
  // nested ternary, so both prettier configs format it identically.)
  let subtitle: string | undefined;
  if (isFixture) {
    subtitle = undefined;
  } else if (isFinished) {
    subtitle = joinParts([showDivision && division, showDate && dateStr, game.venue?.name]);
  } else {
    subtitle = joinParts([
      showDivision && division,
      showDate && dateStr,
      game.scheduledTime,
      game.venue?.name,
      game.venue?.address,
    ]);
  }

  const score =
    isFinished && game.result
      ? formatSetScores(game.result.sets, game.teamIds[0], game.teamIds[1])
      : undefined;

  const isParticipant = game.teamIds.some(id => ctx.myTeamIds.has(id));

  // The result awaits MY approval when I'm a participant, a result exists that
  // isn't verified, and I haven't recorded my own verification yet.
  const awaitingMyApproval =
    !!game.result &&
    !game.result.verified &&
    !!ctx.userId &&
    isParticipant &&
    !game.result.verifications.some(v => v.userId === ctx.userId);

  const activeProposal = game.activeProposal
    ? {
        proposalId: game.activeProposal.id,
        respondingByMe: ctx.myTeamIds.has(game.activeProposal.respondingTeamId),
        slotCount: game.activeProposal.slotCount,
      }
    : null;

  return {
    gameId: game.id,
    teamA: a,
    teamB: b,
    subtitle,
    status: game.status,
    cancellationReason: game.cancellationReason,
    hasResult: !!game.result,
    resultVerified: !!game.result?.verified,
    isParticipant,
    awaitingMyApproval,
    activeProposal,
    score,
    winnerName: game.result?.winner ? teamLabel(game, game.result.winner) : undefined,
  };
}

/**
 * Build the banner input for a `GameProposal` (the negotiation view). The row is
 * treated as a PENDING fixture with an active proposal so it renders "Pending
 * Proposal" and the responder's Approve action, and links to the proposal.
 */
export function proposalToRowInput(proposal: GameProposal, ctx: BannerContext): GameRowInput {
  const slots = activeSlots(proposal);
  const slot = slots[0];
  const division = proposal.divisionName ?? proposal.divisionCategory ?? '';
  const subtitle = joinParts([
    division,
    slot && formatDate(slot.date),
    slot?.time,
    slot?.venue.name,
    slot?.venue.address,
  ]);
  const isParticipant =
    ctx.myTeamIds.has(proposal.proposingTeamId) || ctx.myTeamIds.has(proposal.opposingTeamId);

  return {
    gameId: proposal.gameId ?? proposal.id,
    rowHref: `/games/proposals/${proposal.id}`,
    teamA: proposal.proposingTeamName ?? proposal.proposingTeamId,
    teamB: proposal.opposingTeamName ?? proposal.opposingTeamId,
    subtitle,
    // A proposal corresponds to a PROPOSED fixture ("Pending Proposal").
    status: GameStatus.PROPOSED,
    hasResult: false,
    resultVerified: false,
    isParticipant,
    awaitingMyApproval: false,
    activeProposal: {
      proposalId: proposal.id,
      respondingByMe: ctx.myTeamIds.has(respondingTeamId(proposal)),
      slotCount: slots.length,
    },
  };
}

/**
 * Build the banner input for a cross-tenant `MyGameSummary` (the Home feed).
 * These are always the viewer's own scheduled/finished games — never pending —
 * so there is no proposal or "propose" action.
 */
export function summaryToRowInput(summary: MyGameSummary): GameRowInput {
  const [teamA, teamB] = summary.teams;
  const isFinished =
    summary.status === GameStatus.COMPLETED || summary.status === GameStatus.DISPUTED;
  // Cross-tenant summaries carry no court, so it is omitted here.
  const subtitle = isFinished
    ? joinParts([summary.category, summary.venueName])
    : joinParts([summary.category, summary.scheduledTime, summary.venueName]);
  const score =
    isFinished && summary.result && teamA && teamB
      ? formatSetScores(summary.result.sets, teamA.id, teamB.id)
      : undefined;
  return {
    gameId: summary.gameId,
    teamA: teamA?.name ?? '',
    teamB: teamB?.name ?? '',
    subtitle,
    status: summary.status,
    hasResult: !!summary.result,
    resultVerified: !!summary.result?.verified,
    isParticipant: true,
    awaitingMyApproval: !!summary.awaitingMyApproval,
    score,
    winnerName: summary.result?.winnerTeamId
      ? summary.teams.find(t => t.id === summary.result?.winnerTeamId)?.name
      : undefined,
  };
}
